import { useEffect, useState } from "react";
import api from "../api";
import { useParams, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { CheckCircle, XCircle } from "lucide-react";
import { formatCurrency } from "../utils/currency";

function PaymentAdjustment({ userRole }) {
  const { expenseId, vendorId } = useParams();
  const [data, setData] = useState(null);
  const [actualPaid, setActualPaid] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [isL3ApprovalSent, setIsL3ApprovalSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
 
  const fetchData = async () => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ["User.Read"],
        account: accounts[0],
      });
      const encodedId = encodeURIComponent(encodeURIComponent(expenseId));
      
      // If vendorId is provided in URL, fetch that specific vendor from selected vendors only
      if (vendorId) {
        const res = await api.get(
          `/api/payments/selected-vendors/${encodedId}`,
          { headers: { Authorization: `Bearer ${tokenResponse.accessToken}` } }
        );
        
        const specificVendor = res.data.find(v => v.id.toString() === vendorId);
        if (specificVendor) {
          setData(specificVendor);
          setActualPaid(specificVendor.actual_paid || "");
          return;
        } else {
          setMessage(`Selected vendor with ID ${vendorId} not found.`);
          setError(true);
          return;
        }
      }
      
      // Fetch ONLY selected vendors for this expense
      const res = await api.get(
        `/api/payments/selected-vendors/${encodedId}`,
        { headers: { Authorization: `Bearer ${tokenResponse.accessToken}` } }
      );
      
      if (res.data && res.data.length > 0) {
        if (res.data.length === 1) {
          // Only one selected vendor - show it directly
          setData(res.data[0]);
          setActualPaid(res.data[0].actual_paid || "");
        } else {
          // Multiple selected vendors (rare case) - show selection
          setData({ multipleVendors: res.data });
          setMessage(`Found ${res.data.length} selected vendors. Please choose which vendor to adjust payment for.`);
        }
      } else {
        setMessage('No selected vendor found for this expense.');
        setError(true);
        setData({});
      }
    } catch (err) {
      console.error(err);
      setMessage(err?.response?.data?.message || 'No selected vendor found yet. Ensure final approval is completed and a vendor is selected.');
      setError(true);
      setData({});
    }
  };
 
  useEffect(() => {
    fetchData();
  }, [expenseId, vendorId]);

  const handleVendorSelect = (vendor) => {
    // Navigate to the specific vendor URL instead of just setting state
    navigate(`/payment-adjustment/${encodeURIComponent(expenseId)}/${vendor.id}`);
  };

  const handleSubmit = async () => {
    try {
      // Client-side validation
      if (!receiptFile) {
        setMessage("Please upload a receipt");
        setError(true);
        return;
      }

      // Token used for SharePoint upload (body)
      const graphToken = await instance.acquireTokenSilent({
        scopes: ["https://graph.microsoft.com/.default"],
        account: accounts[0],
      });
      const accessToken = graphToken.accessToken;

      // Token used for backend auth header (needs User.Read for /me)
      const authToken = await instance.acquireTokenSilent({
        scopes: ["User.Read"],
        account: accounts[0],
      });
      const formData = new FormData();
      formData.append("actual_paid", actualPaid);
      formData.append("vendor_id", data.id); // Add vendor ID to identify specific vendor
      formData.append("file", receiptFile);
      formData.append("accessToken", accessToken);

      const encodedId = encodeURIComponent(encodeURIComponent(expenseId));
      const url = `/api/payments/update-actual-paid/${encodedId}`;

      const response = await api.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken.accessToken}`,
        },
      });
      setMessage("Actual paid amount and receipt uploaded successfully.");
      setError(false);
      setReceiptFile(null);
      fetchData();
    } catch (err) {
      console.error(err);
      setMessage("Error submitting the payment update.");
      setError(true);
    }
  };

  // 🔹 NEW: simple navigation handler
  const handleProceedToPayment = (expenseId) => {
    navigate(`/payment/${encodeURIComponent(expenseId)}`);
  };

  const handleSendToL3 = async () => {
    try {
      setIsLoading(true);
      // Acquire a token with both Mail.Send and User.Read so it works for email + backend auth
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ["Mail.Send", "User.Read"],
        account: accounts[0],
      });

      const encodedId2 = encodeURIComponent(encodeURIComponent(expenseId));
      const response = await api.post(
        `/api/payments/send-to-l3/${encodedId2}`,
        {
          requestedBy: accounts[0]?.username,
          accessToken: tokenResponse.accessToken,
          vendor_id: data.id, // Send the specific vendor ID
        },
        { headers: { Authorization: `Bearer ${tokenResponse.accessToken}` } }
      );

      setMessage(response.data.message || "Sent for L3 approval.");
      setError(false);
      setIsL3ApprovalSent(true);
      fetchData();
    } catch (err) {
      console.error(err);
      setMessage("Error sending to L3 for approval.");
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendToPayments = async () => {
    try {
      const graphToken = await instance.acquireTokenSilent({
        scopes: ["https://graph.microsoft.com/.default"],
        account: accounts[0],
      });
      const accessToken = graphToken.accessToken;

      const authToken = await instance.acquireTokenSilent({
        scopes: ["User.Read"],
        account: accounts[0],
      });
      const formData = new FormData();
      formData.append("vendor", JSON.stringify(data));
      formData.append("expenseId", expenseId);
      formData.append("accessToken", accessToken);

      await api.post(
        `/api/payments/send-payment-email`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${authToken.accessToken}`,
          },
        }
      );

      setMessage("Sent to payments team.");
      setError(false);
      setTimeout(() => {
        navigate(`/payment/${expenseId}`);
      }, 1000);
    } catch (err) {
      console.error(err);
      setMessage(
        err.response?.data?.message ||
          "Error sending to payments team. Ensure L3 approved."
      );
      setError(true);
    }
  };
 
  if (error && (!data || Object.keys(data).length === 0)) {
    return (
      <div className="p-6 max-w-xl mx-auto mt-10 rounded-xl shadow-sm border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
        <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text)' }}>Payment Adjustment</h2>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>{message || 'No selected vendor found.'}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <p style={{ color: 'var(--muted)' }}>Loading...</p>
      </div>
    );
  }

  // Show vendor selection if multiple vendors
  if (data.multipleVendors) {
    return (
      <div className="p-6 max-w-4xl mx-auto shadow-sm rounded-xl mt-10 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)', color: 'var(--text)' }}>
        <h2 className="text-2xl font-semibold mb-6 border-b pb-2" style={{ borderColor: 'var(--border)' }}>
          Payment Adjustment - Select Vendor
        </h2>
        
        <p className="mb-4 text-sm" style={{ color: 'var(--muted)' }}>
          {message}
        </p>
        
        <div className="space-y-3">
          {data.multipleVendors.map((vendor) => (
            <div 
              key={vendor.id}
              onClick={() => handleVendorSelect(vendor)}
              className="p-4 border rounded-lg cursor-pointer hover:bg-opacity-50 transition-colors"
              style={{ 
                borderColor: '#22c55e', 
                background: 'rgba(34,197,94,0.05)'
              }}
            >
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">{vendor.vendor_name}</h3>
                    <span className="px-2 py-1 text-xs rounded-full" style={{ background: '#22c55e', color: '#fff' }}>
                      APPROVED
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: 'var(--muted)' }}>
                    Amount: ₹{vendor.cost}
                  </p>
                  {vendor.actual_paid && (
                    <p className="text-sm" style={{ color: '#22c55e' }}>
                      Current Paid: ₹{vendor.actual_paid}
                    </p>
                  )}
                </div>
                <button 
                  className="px-4 py-2 rounded-md text-sm font-medium"
                  style={{ 
                    background: 'rgba(34,197,94,0.15)', 
                    color: '#15803d' 
                  }}
                >
                  Adjust Payment
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
 
  const userRoles = typeof userRole === 'string' ? userRole.split(',').map(r => r.trim()) : [userRole || 'creator'];
  const hasRole = (role) => userRoles.includes(role);
  const isViewOnly = hasRole('approver') || hasRole('payment');

  return (
    <div className="p-6 max-w-4xl mx-auto shadow-sm rounded-xl mt-10 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)', color: 'var(--text)' }}>
      <h2 className="text-2xl font-semibold mb-6 border-b pb-2" style={{ borderColor: 'var(--border)' }}>
        Payment Adjustment - {data.vendor_name}
      </h2>

      <div className="space-y-4 text-sm" style={{ color: 'var(--text)' }}>
        <div>
          <span className="font-medium">Vendor:</span>{" "}
          <span>{data.vendor_name}</span>
        </div>

        <div>
          <span className="font-medium">Approved Amount:</span>{" "}
          <span className="font-semibold" style={{ color: '#22c55e' }}>
            {formatCurrency(data.cost, data.currency || 'INR')}
          </span>
        </div>

        <div>
          <label htmlFor="actualPaid" className="block font-medium mb-2">
            Actual Paid Amount:
          </label>
          <input
            type="number"
            id="actualPaid"
            value={actualPaid}
            onChange={(e) => setActualPaid(e.target.value)}
            className="w-full px-4 py-2 rounded-md focus:outline-none"
            style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)' }}
            placeholder="Enter amount"
            disabled={isViewOnly}
          />
        </div>

        <div>
          <label htmlFor="receipt" className="block font-medium mb-2">
            Upload Receipt (PDF, JPG, PNG)
          </label>
          <input
            type="file"
            id="receipt"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => {
              setReceiptFile(e.target.files[0]);
              // Clear any existing error message when a file is selected
              if (e.target.files[0]) {
                setMessage('');
                setError(false);
              }
            }}
            className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:border-0 file:rounded"
            style={{ color: 'var(--text)' }}
            required
            disabled={isViewOnly}
          />
        </div>

        <div className="pt-4 flex gap-3 flex-wrap">
          {!isViewOnly && (
            <button
              onClick={handleSubmit}
              className="font-semibold px-5 py-2 rounded-md transition border"
              style={{ background: '#8c211e', color: '#ffffff', borderColor: '#721a18' }}
            >
              Update Payment
            </button>
          )}

          {!isViewOnly && (
            <button
              onClick={handleSendToL3}
              disabled={isLoading || data?.status === "approved"}
              className={`font-semibold px-5 py-2 rounded-md transition border ${isLoading || data?.status === "approved" ? 'cursor-not-allowed opacity-60' : ''}`}
              style={isLoading || data?.status === 'approved' ? { background: 'var(--panel)', color: 'var(--muted)', borderColor: 'var(--border)' } : { background: 'rgba(234,179,8,0.18)', color: '#a16207', borderColor: 'transparent' }}
            >
              {isLoading
                ? "Sending..."
                : data?.status === "approved"
                ? "Approved"
                : "Send to L3 Approval"}
            </button>
          )}

          {!isViewOnly && (
            <>
              <button
                onClick={() => handleProceedToPayment(expenseId)}
                className="flex items-center gap-1 px-4 py-2 rounded-md transition text-sm"
                style={{ background: 'rgba(16,185,129,0.18)', color: '#047857' }}
              >
                <CheckCircle className="w-4 h-4" />
                Proceed to Payment
              </button>
            </>
          )}
        </div>

        {message && (
          <div
            className={`mt-4 flex items-center gap-2 text-sm`}
            style={{ color: error ? '#dc2626' : '#16a34a' }}
          >
            {error ? <XCircle size={18} /> : <CheckCircle size={18} />}
            <span>{message}</span>
          </div>
        )}
      </div>
    </div>
  );
}
 
export default PaymentAdjustment;